// Calculation page of the screw-shear-connector module: a port of the reference tool unione-viti-legno-lavoro.html (engine
// in http/screwConnector/formulas; 3D viewer, report and DXF in this folder). Laid out as the reference tool's DS2
// shell inside the platform page: ScrewConnectorForm (the dark sidebar: input panels, result links, tools) at the
// left, ScrewConnectorResults (title row, result cards, sticky results column) at the right; under 1180px the sidebar
// is a drawer opened from the title row.
import cx from "classnames";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type FC } from "react";
import { FormProvider, useForm, useWatch, type Path, type PathValue } from "react-hook-form";
import { Button } from "../../../components/ds2/Button";
import { LoadingSpinner } from "../../../components/ds2/LoadingSpinner";
import { MessageBox } from "../../../components/ds2/MessageBox";
import { ModalDialog } from "../../../components/ds2/ModalDialog";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import type { LoadDurationClassForUser, ServiceClassForUser } from "../../../http/screwConnector/types/normativeTables";
import {
  buildScrewConnectorCalculationDefaults,
  useAutoSaveScrewConnectorCalculation,
  type ScrewConnectorCalculationData,
  type ScrewConnectorCalculationFormValues,
} from "../../../http/screwConnector/useAutoSaveScrewConnectorCalculation";
import { useScrewConnectorSettings } from "../../../http/screwConnector/useSettings";
import type { Calculation } from "../../../http/types/calculations";
import type { LanguageCode } from "../../../types";
import {
  applySolutionPatch,
  buildEngineCatalog,
  changeConnectionType,
  changeThickness,
  runEngine,
  screwRecordOf,
  selectMaterial,
  toControlValues,
  type CalculationInput,
  type ConnectionType,
  type EngineCatalog,
  type EngineOutput,
  type SolutionRow,
} from "../../../http/screwConnector/formulas";
import type { ScrewLevel } from "./ConnectionCard";
import { exportDxf } from "./dxf";
import type { MaterialLevel } from "./ElementCard";
import { missingFieldInfo, ScMissingContext } from "./missingFields";
import type { ReportCardHandle } from "./ReportCard";
import { buildReportContext } from "./reportContext";
import type { ReportVersion } from "./reportPayload";
import styles from "./ScrewConnector.module.css";
import type { ScrewConnector3DViewHandle } from "./ScrewConnector3DView";
import ScrewConnectorForm from "./ScrewConnectorForm";
import ScrewConnectorResults from "./ScrewConnectorResults";
import Tutorial, { type TutorialHost } from "./Tutorial";
import type { TutorialControl } from "./tutorialSteps";
import type { InputSectionId, ResultTarget } from "./types";
import { useEngineView } from "./uiState";
import { useCollapsed } from "./useCollapsed";
import { useReportPdf } from "./useReportPdf";
import { ScUserEditContext } from "./useScForm";
import { useSolutionsSearch } from "./useSolutionsSearch";

export type ScrewConnectorProps = {
  data?: Calculation<ScrewConnectorCalculationData>;
  isAdmin?: boolean;
  // also in the ReadOnlyGate context around the page, read through useReadOnly
  readOnly?: boolean;
  // the page header's buttons area (see Calculation.tsx): unused, this module has no header control
  headerButtonsContainer?: HTMLElement | null;
};

type FormValues = ScrewConnectorCalculationFormValues;

const NO_SERVICE_CLASSES: ServiceClassForUser[] = [];
const NO_LOAD_DURATION_CLASSES: LoadDurationClassForUser[] = [];

// all input panels start collapsed
const DEFAULT_OPEN: Record<InputSectionId, boolean> = {
  secTipologia: false,
  secEl1: false,
  secEl2: false,
  secCarichi: false,
  secConnessione: false,
  secGriglia: false,
};

const NO_MISSING: ReadonlySet<string> = new Set();

// fields whose change does not invalidate the compatible solutions (HTML SOLUTIONS_IGNORED_IDS: screw, grid, centre,
// hole type, washer)
const SOLUTIONS_IGNORED_FIELDS = new Set<string>([
  "screwId",
  "gridRows",
  "gridCols",
  "gridOffsetX",
  "gridOffsetY",
  "gridRowsLinkedToMax",
  "gridColsLinkedToMax",
  "gridOffsetLinkedToMax",
  "preDrilled",
  "washerCode",
]);

const TUTORIAL_FIELD: Record<TutorialControl, keyof FormValues> = {
  thetaNum: "theta",
  b1: "element1Width",
  d1: "element1Depth",
  spessore1: "element1Thickness",
  b2: "element2Width",
  d2: "element2Depth",
  spessore2: "element2Thickness",
  forceFn: "forceFn",
  forceFv: "forceFv",
  forceFa: "forceFa",
};

const sameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a === "number" && typeof b === "number") return Number.isNaN(a) && Number.isNaN(b);
  if ((a && typeof a === "object") || (b && typeof b === "object")) return JSON.stringify(a) === JSON.stringify(b);
  return (a ?? null) === (b ?? null);
};

// screw of the new family / diameter: same diameter (and length) when the family has it, else its first record; the
// engine's cascade then moves it to the first admissible diameter / length (its patch rewrites screwId)
function screwIdFor(input: CalculationInput, catalog: EngineCatalog, level: ScrewLevel, value: string): string | null {
  const v = toControlValues(input, catalog);
  if (level === "family") v.etaFamiglia = value;
  else v.etaDiametro = Number(value);
  const record =
    screwRecordOf(v, catalog) ??
    catalog.SCREWS_DB.find((s) => s.family === v.etaFamiglia && (level === "family" || s.geometria_mm.d1 === v.etaDiametro));
  return record?.id ?? null;
}

// The page owns the data: the catalog, the form (auto-saved calculation input), the engine run and its write-back
// patch, the change handlers that need the catalog, the solution search, the report and the tutorial, and the layout
// state the sidebar and the workspace share (open panels, drawer, collapsed cards). Its two parts are memoized, with
// stable callbacks: they re-render with the engine view, which every input change renews.
const ScrewConnector: FC<ScrewConnectorProps> = ({ data }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule.calculation;
  const dispatch = useContext(NotificationDispatch);
  const readOnly = useReadOnly();

  // ---- catalog ----
  // every list in one call (see useScrewConnectorSettings)
  const settingsSwr = useScrewConnectorSettings();
  const settings = settingsSwr.data;

  useEffect(() => {
    if (settingsSwr.error) dispatch(showErrorNotification(i18n, settingsSwr.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsSwr.error]);

  const catalog = useMemo<EngineCatalog | null>(
    () =>
      settings
        ? buildEngineCatalog({
            families: settings.screwFamilies,
            diameters: settings.screwDiameters,
            screws: settings.screws,
            washers: settings.washers,
            timberMaterials: settings.timberMaterials,
            kmodValues: settings.kmodValues,
            gammaMValues: settings.gammaMValues,
          })
        : null,
    [settings],
  );

  // ---- form ----
  const defaultValues = useMemo<FormValues>(() => buildScrewConnectorCalculationDefaults(data), [data]);
  const formMethods = useForm<FormValues>({ defaultValues, mode: "onChange" });
  const { setValue, getValues } = formMethods;
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const tutorialSnapshot = useRef<FormValues | null>(null);
  const tutorialManualInputMode = useRef<boolean | null>(null);

  // Reset only when another calculation is opened: every auto-save pushes the saved data back through `data`
  // (optimistic update, then the server response), and resetting on those would overwrite what was typed meanwhile.
  useEffect(() => {
    formMethods.reset(buildScrewConnectorCalculationDefaults(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  useAutoSaveScrewConnectorCalculation(data, formMethods.control, { paused: tutorialOpen });
  // the form is created with complete defaultValues, so the watched object is never partial
  const input = useWatch({ control: formMethods.control }) as FormValues;

  const writeField = useCallback(
    (name: string, value: unknown) =>
      setValue(name as Path<FormValues>, value as PathValue<FormValues, Path<FormValues>>, { shouldDirty: true }),
    [setValue],
  );

  // ---- engine ----
  // HTML manualInputMode: false right after "Usa questa" on a solution, true again at the next user edit
  const [manualInputMode, setManualInputMode] = useState(true);
  const engine = useMemo<EngineOutput | null>(() => {
    if (!catalog) return null;
    try {
      return runEngine(input, catalog, { texts: t, manualInputMode });
    } catch (error) {
      console.error("screw connector engine", error);
      return null;
    }
  }, [input, catalog, t, manualInputMode]);

  // write the effective values back (auto distances, linked rows/cols, CLT geometry, ε/γM auto, densities, list
  // fallbacks): the engine patch is a fixed point; the same patch twice in a row would mean it does not converge
  const lastPatch = useRef<string | null>(null);
  useEffect(() => {
    if (!engine || readOnly) return;
    const entries = Object.entries(engine.patch);
    if (!entries.length) {
      lastPatch.current = null;
      return;
    }
    const key = JSON.stringify(engine.patch);
    if (lastPatch.current === key) return;
    lastPatch.current = key;
    for (const [name, value] of entries) {
      if (!sameValue(getValues(name as Path<FormValues>), value)) writeField(name, value);
    }
  }, [engine, readOnly, getValues, writeField]);

  // ---- solutions ----
  const solutions = useSolutionsSearch({ input, catalog, manualInputMode });
  const { invalidate: invalidateSolutions } = solutions;

  // user edits (HTML input/change events): manual input mode again, and the solution table is stale
  const onUserEdit = useCallback(
    (fields: string[]) => {
      setManualInputMode(true);
      if (fields.some((f) => !SOLUTIONS_IGNORED_FIELDS.has(f))) invalidateSolutions();
    },
    [invalidateSolutions],
  );

  // The change handlers read the current form values (getValues), not the render's: stable for the memoized form.
  const applyUserPatch = useCallback(
    (patch: Partial<CalculationInput>) => {
      const names = Object.keys(patch);
      for (const name of names) writeField(name, patch[name as keyof CalculationInput]);
      if (names.length) onUserEdit(names);
    },
    [writeField, onUserEdit],
  );

  const onConnectionTypeChange = useCallback(
    (type: ConnectionType) => {
      if (catalog) applyUserPatch(changeConnectionType(getValues(), catalog, type, t));
    },
    [catalog, applyUserPatch, getValues, t],
  );
  const onMaterialChange = useCallback(
    (n: 1 | 2, level: MaterialLevel, value: string) => {
      if (!catalog) return;
      const change = level === "tipo" ? { tipo: value } : level === "specie" ? { specie: value } : { classe: value };
      applyUserPatch(selectMaterial(getValues(), catalog, n, change, t));
    },
    [catalog, applyUserPatch, getValues, t],
  );
  const onThicknessChange = useCallback(
    (n: 1 | 2, thickness: number | null) => {
      if (catalog) applyUserPatch(changeThickness(getValues(), catalog, n, thickness, t));
    },
    [catalog, applyUserPatch, getValues, t],
  );
  const onScrewChange = useCallback(
    (level: ScrewLevel, value: string) => {
      if (catalog) applyUserPatch({ screwId: screwIdFor(getValues(), catalog, level, value) });
    },
    [catalog, applyUserPatch, getValues],
  );
  // "Usa questa": not a user edit of the form (the table stays), and no penetration warnings until the next edit
  const onApplySolution = useCallback(
    (row: SolutionRow) => {
      const patch = applySolutionPatch(row);
      for (const [name, value] of Object.entries(patch)) writeField(name, value);
      setManualInputMode(false);
    },
    [writeField],
  );

  // ---- 3D, report, DXF ----
  const connectionType: ConnectionType = engine?.values.materiale1 ?? input.connectionType;
  const view3dRef = useRef<ScrewConnector3DViewHandle>(null);
  const captureImages = useCallback(async () => (await view3dRef.current?.captureReportImages()) ?? null, []);

  const state = engine?.state ?? null;
  const blockReason = engine?.blockReason ?? null;
  const owner = data?.owner;
  const projectName = data?.project?.name ?? "";
  const calculationName = data?.name ?? "";
  const [reportDate] = useState(() => new Date());
  const meta = useMemo(
    () => ({
      projectName,
      calculationName,
      author: owner ? `${owner.firstName} ${owner.lastName}`.trim() : null,
      date: reportDate,
    }),
    [projectName, calculationName, owner, reportDate],
  );
  const customName1 = input.element1CustomMaterialName;
  const customName2 = input.element2CustomMaterialName;
  const reportCtx = useMemo(
    () =>
      buildReportContext(
        { connectionType, element1CustomMaterialName: customName1, element2CustomMaterialName: customName2 },
        engine,
        meta,
        t,
      ),
    [connectionType, customName1, customName2, engine, meta, t],
  );

  const reportCardRef = useRef<ReportCardHandle>(null);
  // shared by the report card, the title button and the sidebar menu
  const [reportVersion, setReportVersion] = useState<ReportVersion>("compact");
  const element1MaterialId = input.element1TimberMaterialId;
  const element2MaterialId = input.element2TimberMaterialId;
  const loadDurationClassCode = input.loadDurationClassCode;
  // what the PDF report sends to the server besides the engine's state: the catalog entries it names, the 3D views
  const report = useMemo(
    () => ({
      calculationId: data?.id,
      ctx: reportCtx,
      references: {
        element1TimberMaterialId: element1MaterialId,
        element2TimberMaterialId: element2MaterialId,
        loadDurationClassCode,
      },
      captureImages,
    }),
    [data?.id, reportCtx, element1MaterialId, element2MaterialId, loadDurationClassCode, captureImages],
  );
  const { print: printReport, isPrinting: isReportPrinting } = useReportPdf({
    ...report,
    state,
    blockReason,
  });

  // ---- layout ----
  const view = useEngineView(engine);
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN);
  const [navOpen, setNavOpen] = useState(false);
  const [currentResult, setCurrentResult] = useState<ResultTarget | null>(null);
  const [drawCollapsed, toggleDraw] = useCollapsed("collapsed_drawCard");
  const [solutionsCollapsed, toggleSolutions] = useCollapsed("collapsed_solutionsCard");
  // "Avviare il tutorial?" confirmation (the demo replaces the form data until it is closed)
  const [tutorialConfirm, setTutorialConfirm] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const onSectionToggle = useCallback(
    (id: InputSectionId, open: boolean) => setOpenSections((s) => ({ ...s, [id]: open })),
    [],
  );
  const onToggleNav = useCallback(() => setNavOpen((open) => !open), []);

  // opens the field's panel (and the drawer), then scrolls to it and focuses it (HTML "missing fields" links)
  const onFocusField = useCallback((control: string, section: InputSectionId) => {
    setOpenSections((s) => ({ ...s, [section]: true }));
    setNavOpen(true);
    requestAnimationFrame(() => {
      const named = sidebarRef.current?.querySelector<HTMLElement>(`[name="${control}"]`);
      if (!named) return;
      // react-select keeps the name on a hidden input inside its box: the box is the field
      const box = named instanceof HTMLInputElement && named.type === "hidden" ? named.parentElement : null;
      (box ?? named).scrollIntoView({ block: "center" });
      (box ? box.querySelector<HTMLElement>("input:not([type=hidden])") : named)?.focus({ preventScroll: true });
    });
  }, []);

  const solutionsResult = solutions.result;
  const missing = useMemo<ReadonlySet<string>>(
    () => (solutionsResult?.kind === "missing" ? new Set(solutionsResult.missing) : NO_MISSING),
    [solutionsResult],
  );
  // the panel of the first missing field opens by itself
  useEffect(() => {
    if (solutionsResult?.kind !== "missing") return;
    const first = solutionsResult.missing[0];
    if (first) {
      const { section } = missingFieldInfo(first, t);
      setOpenSections((s) => ({ ...s, [section]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutionsResult]);

  // sidebar "Risultati" links: the card (opened first when collapsed, by its own toggle as the HTML) scrolls into view
  const onResultLink = useCallback(
    (target: ResultTarget) => {
      setNavOpen(false);
      setCurrentResult(target);
      const el = document.getElementById(target);
      if (!el) return;
      if (target === "solutionsCard" && solutionsCollapsed) toggleSolutions();
      if (target === "posCard") el.querySelector<HTMLElement>("details:not([open]) > summary")?.click();
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    },
    [solutionsCollapsed, toggleSolutions],
  );

  // sidebar tools and title button, in the language picked from their menu; without a version: the one selected in the
  // report card
  const onGenerateReport = useCallback(
    (version: ReportVersion | undefined, language: LanguageCode) => {
      setNavOpen(false);
      if (version) setReportVersion(version);
      void printReport(version ?? reportVersion, language);
    },
    [printReport, reportVersion],
  );
  const onTitleReport = useCallback(
    (language: LanguageCode) => onGenerateReport(undefined, language),
    [onGenerateReport],
  );
  const onExportDxf = useCallback(() => {
    setNavOpen(false);
    exportDxf(state, reportCtx, blockReason);
  }, [state, reportCtx, blockReason]);
  const onStartTutorial = useCallback(() => {
    setNavOpen(false);
    setTutorialConfirm(true);
  }, []);

  // tutorial: opens an input panel, expands a card
  const openSection = (id: InputSectionId) => setOpenSections((s) => ({ ...s, [id]: true }));
  const expandCard = (id: "drawCard" | "solutionsCard") => {
    if (id === "drawCard" && drawCollapsed) toggleDraw();
    if (id === "solutionsCard" && solutionsCollapsed) toggleSolutions();
  };

  // The demo runs on example data only (HTML: apply(DEFAULTS)): the user's values are set aside, the form starts
  // from the initial values of a new calculation (required fields empty), and everything is put back on close. The
  // auto-save is paused meanwhile, so the example data never reaches the saved calculation.
  const startTutorial = () => {
    if (readOnly) return;
    const snapshot = structuredClone(getValues());
    tutorialSnapshot.current = snapshot;
    tutorialManualInputMode.current = manualInputMode;
    setTutorialOpen(true);
    setManualInputMode(true);
    formMethods.reset({ ...buildScrewConnectorCalculationDefaults(), unitSystem: snapshot.unitSystem });
  };
  const closeTutorial = () => {
    const snapshot = tutorialSnapshot.current;
    const manualMode = tutorialManualInputMode.current;
    tutorialSnapshot.current = null;
    tutorialManualInputMode.current = null;
    setTutorialOpen(false);
    if (snapshot) formMethods.reset(snapshot);
    if (manualMode !== null) setManualInputMode(manualMode);
  };
  // The demo makes several edits in a row, faster than the renders: the host always reads the current form values
  // (getValues) and the latest engine output, never the ones captured by the render that created it.
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const tutorialHost: TutorialHost = {
    type: (control, value) => {
      if (control === "spessore1" || control === "spessore2") {
        // same path as a thickness edit (particleboard ρk depends on the thickness)
        if (catalog) applyUserPatch(changeThickness(getValues(), catalog, control === "spessore1" ? 1 : 2, value, t));
        return;
      }
      const name = TUTORIAL_FIELD[control];
      writeField(name, value);
      onUserEdit([name]);
    },
    isEmpty: (control) => {
      const disabled = engineRef.current?.ui.disabled as Partial<Record<string, boolean>> | undefined;
      if (disabled?.[control]) return false;
      const value = getValues(TUTORIAL_FIELD[control]);
      return value === null || value === undefined || (typeof value === "number" && Number.isNaN(value));
    },
    connectionType: () => getValues("connectionType"),
    setConnectionType: (type) => {
      if (catalog) applyUserPatch(changeConnectionType(getValues(), catalog, type, t));
    },
    thickness1: () => getValues("element1Thickness"),
    openReportPreview: async () => {
      await reportCardRef.current?.openPreview();
    },
    closeReportPreview: () => reportCardRef.current?.closePreview(),
    isReportPreviewOpen: () => reportCardRef.current?.isPreviewOpen() ?? false,
  };

  if (!catalog) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner delayed={200} />
      </div>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <ScUserEditContext value={onUserEdit}>
        <ScMissingContext value={missing}>
          <div className={cx(styles.root, navOpen && styles.navOpen)}>
            <div ref={sidebarRef} className={styles.sidebarSlot}>
              <ScrewConnectorForm
                view={view}
                connectionType={connectionType}
                serviceClasses={settings?.serviceClasses ?? NO_SERVICE_CLASSES}
                loadDurationClasses={settings?.loadDurationClasses ?? NO_LOAD_DURATION_CLASSES}
                onConnectionTypeChange={onConnectionTypeChange}
                onMaterialChange={onMaterialChange}
                onThicknessChange={onThicknessChange}
                onScrewChange={onScrewChange}
                openSections={openSections}
                onSectionToggle={onSectionToggle}
                currentResult={currentResult}
                onResultLink={onResultLink}
                onGenerateReport={onGenerateReport}
                onExportDxf={onExportDxf}
                onStartTutorial={onStartTutorial}
              />
            </div>
            <ScrewConnectorResults
              view={view}
              connectionType={connectionType}
              navOpen={navOpen}
              onToggleNav={onToggleNav}
              onGenerateReport={onTitleReport}
              isReportPrinting={isReportPrinting}
              reportVersion={reportVersion}
              onReportVersionChange={setReportVersion}
              report={report}
              reportCardRef={reportCardRef}
              view3dRef={view3dRef}
              drawCollapsed={drawCollapsed}
              onToggleDraw={toggleDraw}
              solutionsCollapsed={solutionsCollapsed}
              onToggleSolutions={toggleSolutions}
              solutions={solutions}
              onApplySolution={onApplySolution}
              onFocusField={onFocusField}
            />
          </div>
          {tutorialConfirm ? (
            <ModalDialog
              title={t.tutorial.confirmTitle}
              onClose={() => setTutorialConfirm(false)}
              buttons={[
                <Button key="cancel" variant="secondary" onClick={() => setTutorialConfirm(false)}>
                  {t.tutorial.cancel}
                </Button>,
                <Button
                  key="start"
                  data-autofocus
                  onClick={() => {
                    setTutorialConfirm(false);
                    startTutorial();
                  }}
                >
                  {t.tutorial.start}
                </Button>,
              ]}
            >
              <MessageBox type="warning">{t.tutorial.confirmText}</MessageBox>
            </ModalDialog>
          ) : null}
          {tutorialOpen ? (
            <Tutorial host={tutorialHost} layout={{ openSection, setNavOpen, expandCard }} onClose={closeTutorial} />
          ) : null}
        </ScMissingContext>
      </ScUserEditContext>
    </FormProvider>
  );
};

export default ScrewConnector;
